import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { PermissionGuard } from './guards/permission.guard';
import { LoggedInGuard } from './guards/logged-in.guard';
import { AuthGuard } from './guards/auth.guard';
import { ApicallService } from './services/apicall.service';
import { PANNumberValidationGuard } from './guards/pannumber-validation.guard';
import { ForeignAgentValidationGuard } from './guards/foreign-agent-validation.guard';


const routes: Routes = [
  {
    path: '',
    redirectTo: 'agent-login',
    pathMatch: 'full',
  },
  {
    path: 'maintenance',
    loadChildren: () =>
      import('./modules/maintenance/maintenance.module').then(
        (m) => m.MaintenancePageModule
      ),
  },
  {
    path: 'staff-login',
    loadChildren: () =>
      import('./modules/login/login.module').then((m) => m.LoginPageModule),
    canActivate: [LoggedInGuard],
  },
  {
    path: 'reset-password/:token',
    loadChildren: () =>
      import('./modules/reset-password/reset-password.module').then(
        (m) => m.ResetPasswordPageModule
      ),
    canActivate: [LoggedInGuard],
  },
  {
    path: 'forgot-password',
    loadChildren: () =>
      import('./modules/forget-password/forget-password.module').then(
        (m) => m.ForgetPasswordPageModule
      ),
    canActivate: [LoggedInGuard],
  },
  {
    path: 'agent-login',
    loadChildren: () =>
      import('./modules/agent-login/agent-login.module').then(
        (m) => m.AgentLoginPageModule
      ),
    canActivate: [LoggedInGuard],
  },
  {
    path: 'agents',
    loadChildren: () =>
      import('./modules/agents/agents.module').then((m) => m.AgentsPageModule),
    canActivate: [PermissionGuard],
    data: { title: 'Agents', permission: ['/user/get_agent_list'] },
  },
  {
    path: 'cache-activity',
    loadChildren: () =>
      import('./modules/cache-activity/cache-activity.module').then((m) => m.CacheActivityPageModule),
    canActivate: [PermissionGuard],
    data: { title: 'Agents', permission: ['/user/get_agent_list'] },
  },

  {
    path: 'campaigns',
    loadChildren: () =>
      import('./modules/campaigns/campaigns.module').then(
        (m) => m.CampaignsPageModule
      ),
    canActivate: [PermissionGuard],
    data: { permission: ['/campaign/list_campaign'], title: 'Campaigns' },
  },
  {
    path: 'plb-campaign',
    loadChildren: () =>
      import('./modules/plb-campaign-list/plb-campaign-list.module').then(
        (m) => m.PLBCampaignPageModule
      ),
    canActivate: [PermissionGuard],
    data: { permission: ['/campaign/list_campaign'], title: 'PLBCampaigns' },
  },
  {
    path: 'incentives',
    loadChildren: () =>
      import('./modules/incentives/incentives.module').then(
        (m) => m.IncentivesPageModule
      ),
    canActivate: [PermissionGuard, PANNumberValidationGuard,ForeignAgentValidationGuard],
    data: {
      permission: [
        '/agent/get_incentive_list',
        '/agent/get_incentive_list_child',
        '/user/get_incentive_list',
      ],
      title: 'Campaigns',
    },
  },
  {
    path: 'foreign-incentives',
    loadChildren: () =>
      import('./modules/foreign-incentives/incentives.module').then(
        (m) => m.IncentivesPageModule
      ),
    canActivate: [PermissionGuard, PANNumberValidationGuard],
    data: {
      permission: [
        '/agent/get_incentive_list',
        '/agent/get_incentive_list_child',
        '/user/get_incentive_list',
      ],
      title: 'Campaigns',
    },
  },
  {
    path: 'profile',
    loadChildren: () =>
      import('./modules/profile/profile.module').then(
        (m) => m.ProfilePageModule
      ),
    canActivate: [PermissionGuard],
    data: {
      title: 'Profile',
      permission: [
        '/agent/get_agent_details',
        '/user/get_agent_details_by_org_id',
        '/agent/get_agent_details_by_org_id',
      ],
    },
  },
  {
    path: 'edit-profile',
    loadChildren: () =>
      import('./modules/edit-profile/edit-profile.module').then(
        (m) => m.EditProfilePageModule
      ),
    canActivate: [PermissionGuard],
    data: {
      title: 'Edit Profile',
      permission: ['/agent/update_agent_gst_tax_details'],
    },
  },
  {
    path: 'unmapped-org',
    loadChildren: () =>
      import('./modules/unmapped-org/unmapped-org.module').then(
        (m) => m.UnmappedOrgPageModule
      ),
    canActivate: [PermissionGuard],
    data: {
      title: 'Edit Profile',
      permission: ['/user/get_unmapped_org_list'],
    },
  },
  {
    path: 'permission-denied',
    loadChildren: () =>
      import('./modules/permission-denied/permission-denied.module').then(
        (m) => m.PermissionDeniedPageModule
      ),
  },
  {
    path: 'run-campaign',
    loadChildren: () =>
      import('./modules/run-campaign/run-campaign.module').then(
        (m) => m.RunCampaignPageModule
      ),
    canActivate: [PermissionGuard],
    data: { permission: ['/campaign/get_campaign_detail'] },
  },
  {
    path: 'run-plb-campaign',
    loadChildren: () =>
      import('./modules/run-plb-campaign/run-plb-campaign.module').then(
        (m) => m.RunPlbCampaignPageModule
      ),
    canActivate: [PermissionGuard],
    data: { permission: ['/campaign/get_campaign_detail'] },
  },
  {
    path: 'run-direct-campaign',
    loadChildren: () =>
      import('./modules/run-direct-campaign/run-direct-campaign.module').then(
        (m) => m.RunDirectCampaignPageModule
      ),
    canActivate: [PermissionGuard],
    data: { permission: ['/campaign/get_campaign_detail'] },
  },

  {
    path: 'campaign-execution',
    loadChildren: () =>
      import('./modules/campaign-execution/campaign-execution.module').then(
        (m) => m.CampaignExecutionPageModule
      ),
    canActivate: [PermissionGuard],
    data: { permission: ['/campaign/get_campaign_execution_list'] },
  },
  {
    path: 'plb-campaign-execution',
    loadChildren: () =>
      import('./modules/plb-campaign-execution/plb-campaign-execution.module').then(
        (m) => m.PLBCampaignExecutionPageModule
      ),
    canActivate: [PermissionGuard],
    data: { permission: ['/campaign/get_campaign_execution_list'] },
  },
  {
    path: 'org-mapped',
    loadChildren: () =>
      import('./modules/org-mapped/org-mapped.module').then(
        (m) => m.OrgMappedPageModule
      ),
    canActivate: [PermissionGuard],
    data: {
      permission: [
        '/agent/org_mapping_by_agent_id',
        '/agent/org_mapping_by_org_ref_id',
        '/user/org_mapped',
      ],
    },
  },
  {
    path: 'company-list',
    loadChildren: () =>
      import('./modules/company-list/company-list.module').then(
        (m) => m.CompanyListPageModule
      ),
    canActivate: [PermissionGuard],
    data: {
      permission: [
        '/user/get_company_name_list',
      ],
    },
  },
  {
    path: 'raise-invoice',
    loadChildren: () =>
      import('./modules/raise-invoice/raise-invoice.module').then(
        (m) => m.RaiseInvoicePageModule
      ),
    canActivate: [PermissionGuard, PANNumberValidationGuard],
    data: { permission: ['/agent/get_incentive_details_by_ids'] },
  },
  {
    path: 'approve-invoice',
    loadChildren: () =>
      import('./modules/approve-invoice/approve-invoice.module').then(
        (m) => m.ApproveInvoicePageModule
      ),
    canActivate: [PermissionGuard],
    data: { permission: ['/user/incentive_invoice_details'] },
  },
  {
    path: 'view-invoice',
    loadChildren: () =>
      import('./modules/view-invoice/view-invoice.module').then(
        (m) => m.ViewInvoicePageModule
      ),
    canActivate: [PermissionGuard],
    data: {
      permission: [
        '/user/incentive_invoice_details',
        '/agent/incentive_invoice_details',
      ],
    },
  },
  {
    path: 'gst-invoice-list',
    loadChildren: () =>
      import('./modules/gst-invoice-list/gst-invoice-list.module').then(
        (m) => m.GstInvoiceListPageModule
      ),
    canActivate: [PermissionGuard],
    data: { permission: ['/user/incentive_invoices_list'] },
  },
  {
    path: 'non-gst-invoice-list',
    loadChildren: () =>
      import('./modules/urd-invoice-list/urd-invoice-list.module').then(
        (m) => m.UrdInvoiceListPageModule
      ),
    canActivate: [PermissionGuard],
    data: { permission: ['/user/incentive_invoices_list'] },
  },
  {
    path: 'foreign-invoice-list',
    loadChildren: () =>
      import('./modules/foreign-invoice-list/foreign-invoice-list.module').then(
        (m) => m.ForeignInvoiceListPageModule
      ),
    canActivate: [PermissionGuard],
    data: { permission: ['/user/incentive_invoices_list'] },
  },
  {
    path: 'invoices',
    loadChildren: () =>
      import('./modules/invoices/invoices.module').then(
        (m) => m.InvoicesPageModule
      ),
    canActivate: [PermissionGuard, PANNumberValidationGuard],
    data: { permission: ['/agent/incentive_invoices_list'] },
  },
  {
    path: 'import-report',
    loadChildren: () =>
      import('./modules/import-report/import-report.module').then(
        (m) => m.ImportReportPageModule
      ),
    canActivate: [PermissionGuard],
    data: { permission: ['/incentive/list_import_incentive_report'] },
  },
  {
    path: 'configuration',
    loadChildren: () =>
      import('./modules/configuration/configuration.module').then(
        (m) => m.ConfigurationPageModule
      ),
    canActivate: [PermissionGuard],
    data: { permission: ['/user/add_system_config'] },
  },
  {
    path: 'raised-dispute',
    loadChildren: () =>
      import('./modules/raise-dispute/raise-dispute.module').then(
        (m) => m.RaiseDisputePageModule
      ),
    canActivate: [PermissionGuard, PANNumberValidationGuard],
    data: {
      permission: [
        '/agent/dispute_list',
        '/agent/dispute_list_child',
        '/user/dispute_list_sales',
        '/user/dispute_list_finance',
      ],
    },
  },
  {
    path: 'profile-verification',
    loadChildren: () =>
      import('./modules/profile-verification/profile-verification.module').then(
        (m) => m.ProfileVerificationPageModule
      ),
    canActivate: [PermissionGuard],
    data: { permission: ['/user/list_agent_verification_profile'] },
  },
  {
    path: 'navitaire-export',
    loadChildren: () =>
      import('./modules/navitaire-export/navitaire-export.module').then(
        (m) => m.NavitaireExportPageModule
      ),
    canActivate: [PermissionGuard],
    data: { permission: ['/user/get_navitaire_export_list'] },
  },
  {
    path: 'ltdc-report',
    loadChildren: () =>
      import('./modules/ltds-report/ltds-report.module').then(
        (m) => m.LtdsReportPageModule
      ),
    canActivate: [PermissionGuard],
    data: { permission: ['/user/get_ltdc_list'] },
  },
  {
    path: 'dashboard',
    loadChildren: () =>
      import('./modules/dashboard/dashboard.module').then(
        (m) => m.DashboardPageModule
      ),
    canActivate: [PermissionGuard, PANNumberValidationGuard],
    data: {
      permission: ['/agent/dashboard_details', '/user/get_incentive_list'],
    },
  },
  {
    path: 'upload-mapping',
    loadChildren: () =>
      import('./modules/upload-mapping/upload-mapping.module').then(
        (m) => m.UploadMappingPageModule
      ),
    canActivate: [PermissionGuard],
    data: { permission: ['/user/get_upload_mapping_list'] },
  },
  {
    path: 'company-upload',
    loadChildren: () =>
      import('./modules/company-upload/company-upload.module').then(
        (m) => m.CompanyUploadPageModule
      ),
    canActivate: [PermissionGuard],
    data: { permission: ['/user/get_upload_mapping_list'] },
  },
  {
    path: 'erp-extract',
    loadChildren: () =>
      import('./modules/erp-export/erp-export.module').then(
        (m) => m.ErpExportPageModule
      ),
    canActivate: [PermissionGuard],
    data: { permission: ['/user/get_erp_export_list'] },
  },
  {
    path: '404',
    loadChildren: () =>
      import('./modules/notfound/notfound.module').then(
        (m) => m.NotfoundPageModule
      ),
  },
  {
    path: 'urd-acknowledge-page',
    loadChildren: () =>
      import('./modules/urd-acknowledge-page/urd-acknowledge-page.module').then(
        m => m.UrdAcknowledgePagePageModule
      ),
  },
  {
    path: '**',
    redirectTo: '/404',
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules }),
  ],
  exports: [RouterModule],
  providers: [PermissionGuard, LoggedInGuard, ApicallService, AuthGuard],
})
export class AppRoutingModule { }
